import { IcebergButton } from '@tradesolution/iceberg-ui-react';
import { useContext, useRef, useState } from 'react';
import { Alert, Form, Modal, Table } from 'react-bootstrap';
import QAApi from 'services/QAApi';
import { FileUploadResponse } from 'services/QAApi/types';
import './styleTable.scss';
import { useMsal } from '@azure/msal-react';
// XLSX for Excel file generation
import * as XLSX from 'xlsx';

interface Props {}

const ChangeRequestUploadModal = (props: Props) => {
  // Column headers from EnhetEndringInfo
  // Headers from EnhetEndringInfo, capitalized
  const excelHeaders = [
    'Gln',
    'EnhetNavn',
    'FirmaNavn',
    'Organisasjonsnummer',
    'BesokAdresse',
    'BesokPostnummer',
    'BesokPoststed',
    'MedlemHos',
    'MedlemsNummer',
    'MedlemFraOgMedDato',
    'MedlemTilOgMedDato',
    'Telefon',
    'FakturaAdresse',
    'FakturaPostnummer',
    'FakturaPoststed',
    'PostAdresse',
    'Postnummer',
    'PostPoststed',
    'Kommentar',
    'GrossistKundenummer',
    'GrossistKundenummerHos',
    'Kontonummer',
    'Salgsareal',
    'FakturaEpost',
    'Epost',
  ];

  const handleDownloadTemplate = () => {
    // Create worksheet with headers only
    const ws = XLSX.utils.aoa_to_sheet([excelHeaders]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Endringsmeldinger');
    XLSX.writeFile(wb, 'Endringsmeldinger_template.xlsx');
  };

  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<FileUploadResponse>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [useAllRows, setUseAllRows] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showTips, setShowTips] = useState(false);

  const { accounts } = useMsal();
  const username = accounts[0] && accounts[0].name;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Vennligst velg en fil først');
      return;
    }

    try {
      setIsUploading(true);
      setSuccessMessage(null);
      setError(null);
      const results = await QAApi.mapExcelToEndringsmelding(selectedFile, useAllRows);
      setUploadResults(results);
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Det oppstod en feil ved opplasting av filen. Vennligst prøv igjen.');
    } finally {
      setIsUploading(false);
    }
  };

  const sendToQa = async () => {
    if (selectedRows.length === 0) {
      setError('Vennligst velg en eller flere rader først');
      return;
    }

    try {
      setIsUploading(true);
      setError(null);
      setSuccessMessage(null);
      let command = {
        membershipOwnerId: uploadResults.membershipOwner.id,
        meldingerToSend: selectedRows.map((index) => uploadResults.meldinger[index]),
        membershipDocumentChainId: uploadResults.membershipDocumentChainId,
        sentBy: username,
      };
      await QAApi.sendFilMeldingerToQa(command);
      //Log the successful upload
      setSuccessMessage(
        `${selectedRows.length} rad(er) fra ${uploadResults.membershipOwner.name} sendt til QA`,
      );
      setUploadResults(null);
      setSelectedRows([]);
    } catch (err) {
      console.error('Error sending to qa', err);
      setError('Det oppstod en feil ved oversendelse til QA. Vennligst prøv igjen.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedFile(null);
    setUploadResults(null);
    setError(null);
    setSuccessMessage(null);
    setSelectedRows([]);
    setUseAllRows(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setShowModal(false);
  };

  const handleRowCheck = (index: number) => {
    setSelectedRows((prev) => {
      if (prev.includes(index)) {
        return prev.filter((rowIndex) => rowIndex !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleSelectAllRows = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedRows(uploadResults?.meldinger?.map((_, index) => index));
    } else {
      setSelectedRows([]);
    }
  };
  return (
    <>
      <IcebergButton
        className="w-100"
        icon="plus"
        variant="outline-primary"
        onClick={() => setShowModal(true)}
      >
        Last opp liste
      </IcebergButton>
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        fullscreen={uploadResults?.meldinger?.length > 0 ? true : undefined}
        size={uploadResults?.meldinger?.length > 0 ? undefined : 'xl'}
        dialogClassName={uploadResults?.meldinger?.length > 0 ? 'modal-90w' : ''}
      >
        <Modal.Header closeButton>
          <Modal.Title>Excel opplasting</Modal.Title>
          <div style={{ position: 'absolute', right: 60, top: 16 }}>
            <IcebergButton
              icon="import"
              variant="outline-primary"
              size="sm"
              onClick={handleDownloadTemplate}
            >
              Last ned mal
            </IcebergButton>
          </div>
        </Modal.Header>
        <Modal.Body>
          {!uploadResults?.meldinger?.length ? (
            <div className="d-flex flex-column">
              <Form>
                <Form.Group controlId="formFile" className="mb-3">
                  <Form.Label>Velg en Excel-fil med endringsmeldinger</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    accept=".xlsx,.xls"
                  />
                  <Form.Text className="text-muted">Støttede filtyper: .xlsx, .xls</Form.Text>
                  <br />
                  <Form.Text className="text-muted">
                    Obs: Excel ark med flere rader med overskrifter støttes ikke og vil gi
                    uforutsigbare resultater (eks: Kjøpmanshuset medlemsendringer)
                  </Form.Text>
                  <br />
                  <Form.Check
                    type="checkbox"
                    id="useAllRows"
                    label="Les inn alle rader fra totalfil (ikke endringer)"
                    checked={useAllRows}
                    onChange={(e) => setUseAllRows(e.target.checked)}
                    className="mt-2 mb-2"
                  />
                  <a
                    href="#"
                    style={{ fontSize: '1rem', marginTop: '8px', display: 'inline-block' }}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowTips((t) => !t);
                    }}
                  >
                    {showTips ? 'Skjul tips' : 'Vis tips'}
                  </a>
                  {showTips && (
                    <div
                      style={{
                        background: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px',
                        padding: '16px',
                        marginTop: '8px',
                      }}
                    >
                      <h5>Tips for opplasting av Excel-filer</h5>
                      <ul>
                        <li>
                          <b>Filnavn:</b> Inkluder navnet på innsenderen (f.eks. Konsum eller MIX) i
                          filnavnet. Dette sikrer at historikk for totalfiler blir korrekt lagret
                        </li>
                        <li>
                          <b>Ny totalfil:</b> Ved første innlesning av en totalfil fra en ny kilde,
                          vises kun den nyeste raden (basert på "Medlem fra og med") og alle enheter
                          som ble medlem samme måned. For å få inn flere rader, lag en kopi av
                          Excel-arket og fjern alle nye medlemmer unntatt én. Send denne til QA.
                          Deretter kan du laste opp den opprinnelige filen, og resterende medlemmer
                          vil vises etter opplastning
                        </li>
                        <li>
                          <b>Manglende inn-/utmeldingsdatoer:</b> Hvis en totalliste mangler datoer,
                          vises alle rader etter opplastning. Dette kan brukes for å legge inn flere
                          enheter fra et Excel-ark
                        </li>
                        <li>
                          <b>Hvilket ark brukes:</b> Hvis Excel-filen har både innmeldings- og
                          utmeldingsark, brukes disse. Mangler ett av dem, brukes totalarket. For å
                          finne endringer i totalark, så brukes tidligere innlesninger fra samme
                          innsender
                        </li>
                        <li>
                          <b>Flere inn-/utmeldingsark:</b> Hvis det finnes flere innmeldings- eller
                          utmeldingsark, brukes det arket med færrest rader
                        </li>
                        <li>
                          <b>IMS:</b> Hvis IMS-filen inneholder en kategori kolonne, så må alle
                          rader som ikke har en "Medlem" kategori fjernes
                        </li>
                        <li>
                          <b>Sport Holding:</b> Veldig komplisert fil så denne må håndteres manuelt.
                          Hvis det er ønskelig å laste opp noe av det, så kan du lage egne Excel-ark
                          per tab og per "avsnitt". Prøv også å klage til de for å få bedre
                          dokumenter :)
                        </li>
                      </ul>
                    </div>
                  )}
                </Form.Group>
              </Form>
              {error && <Alert variant="danger">{error}</Alert>}
              {successMessage && <Alert variant="success">{successMessage}</Alert>}
              {uploadResults && uploadResults.meldinger?.length === 0 && !isUploading && (
                <Alert variant="success">Ingen endringer fra forrige opplastning</Alert>
              )}
              {isUploading && (
                <div className="text-center my-4">
                  <p className="mt-2">Laster opp og behandler filen...</p>
                </div>
              )}
              {!isUploading && !uploadResults?.meldinger?.length && (
                <div className="text-center my-4">
                  <svg
                    width="300"
                    height="250"
                    viewBox="0 0 420 350"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clip-path="url(#clip0_3_9553)">
                      <path
                        d="M134.587 94.759L218.394 72.3029C219.355 72.0455 220.374 72.1561 221.255 72.608L221.43 72.7026L262.863 96.6241C263.782 97.1545 264.453 98.028 264.727 99.0526L299.869 230.203C302.791 241.109 296.319 252.319 285.413 255.241L184.811 282.198C173.905 285.12 162.695 278.648 159.773 267.742L120.131 119.797C117.209 108.891 123.681 97.6812 134.587 94.759Z"
                        fill="#84A881"
                        stroke="#1C355A"
                        stroke-width="2"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M222.322 75.0466C222.237 74.2612 223.094 73.6634 223.818 74.0808L260.475 95.2459C261.247 95.6914 261.095 96.8472 260.234 97.0778L234.221 104.048C232.087 104.62 229.894 103.353 229.322 101.22L222.352 75.2056L222.322 75.0466Z"
                        fill="#5A8F57"
                        stroke="#1C355A"
                        stroke-width="2"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M227.86 214.817C229.062 214.495 230.205 214.318 231.283 214.298L231.284 214.298C232.333 214.265 233.279 214.327 234.109 214.497C234.609 214.599 234.952 215.062 234.904 215.57L234.564 219.156C234.538 219.425 234.404 219.673 234.193 219.842C233.981 220.011 233.71 220.087 233.441 220.053C232.887 219.982 232.33 219.939 231.769 219.922L231.208 219.913C230.492 219.915 229.843 219.998 229.254 220.156C228.278 220.418 227.596 220.83 227.137 221.348C226.693 221.858 226.404 222.536 226.313 223.441L226.291 223.799C226.265 224.657 226.4 225.685 226.725 226.897C227.082 228.228 227.543 229.248 228.081 229.993C228.607 230.713 229.192 231.151 229.829 231.376C230.462 231.589 231.215 231.608 232.127 231.364C233.102 231.102 233.915 230.758 234.58 230.342L234.842 230.174C235.448 229.775 236.004 229.333 236.512 228.848C236.767 228.604 237.133 228.512 237.473 228.608C237.813 228.703 238.077 228.971 238.168 229.312L239.115 232.849C239.208 233.195 239.109 233.566 238.855 233.818C238.171 234.495 237.398 235.064 236.54 235.527L236.542 235.528C235.671 236.011 234.592 236.415 233.329 236.754C231.51 237.241 229.784 237.348 228.169 237.036L227.848 236.969C226.217 236.594 224.803 235.756 223.616 234.491L223.382 234.233C222.143 232.818 221.233 230.91 220.607 228.573C219.955 226.139 219.806 223.952 220.244 222.053L220.33 221.705C220.789 219.983 221.654 218.532 222.931 217.383L222.934 217.381L223.189 217.161C224.484 216.078 226.05 215.302 227.86 214.817ZM245.042 210.213C246.886 209.719 248.63 209.66 250.245 210.09L250.544 210.176C252.023 210.627 253.3 211.451 254.361 212.63C255.494 213.89 256.302 215.502 256.817 217.421L257.402 219.606C257.545 220.139 257.228 220.688 256.695 220.831L244.926 223.984C245.406 225.122 246.04 225.881 246.787 226.344L246.966 226.446C247.877 226.934 249.004 227.046 250.434 226.662C251.455 226.389 252.341 226.061 253.101 225.684L253.419 225.519C254.276 225.045 255.143 224.458 256.016 223.751C256.28 223.539 256.632 223.473 256.954 223.578C257.276 223.683 257.522 223.943 257.61 224.269L258.498 227.581C258.598 227.956 258.473 228.354 258.177 228.604C257.394 229.266 256.566 229.834 255.694 230.308L255.318 230.506C254.301 231.02 253.069 231.464 251.638 231.848C249.628 232.386 247.71 232.483 245.903 232.101L245.894 232.1C244.075 231.697 242.493 230.796 241.163 229.421L241.159 229.417C239.826 228.021 238.876 226.155 238.266 223.879C237.662 221.624 237.513 219.543 237.874 217.66C238.233 215.789 239.032 214.188 240.287 212.897L240.526 212.66C241.739 211.501 243.256 210.692 245.042 210.213ZM193.575 217.797L193.676 217.775C194.176 217.694 194.666 218.004 194.8 218.504L195.738 222.006C195.881 222.539 195.565 223.088 195.031 223.231L185.803 225.703L187.13 230.654L195.716 228.354C196.25 228.211 196.798 228.527 196.941 229.061L197.875 232.545C198.018 233.079 197.701 233.627 197.168 233.77L188.581 236.071L190.196 242.098L199.424 239.625C199.958 239.482 200.506 239.798 200.649 240.332L201.592 243.851C201.735 244.385 201.418 244.933 200.885 245.076L186.53 248.922C185.996 249.065 185.448 248.749 185.305 248.215L178.513 222.868L178.491 222.768C178.41 222.268 178.72 221.778 179.22 221.644L193.575 217.797ZM202.767 221.93L202.89 221.906C203.179 221.867 203.474 221.955 203.694 222.153L208.746 226.695L210.866 220.23L210.912 220.114C211.037 219.85 211.271 219.652 211.556 219.575L216.186 218.334C216.545 218.239 216.927 218.35 217.179 218.622C217.431 218.895 217.511 219.285 217.387 219.635L213.68 230.093L222.564 237.643C222.847 237.884 222.974 238.262 222.893 238.625C222.811 238.988 222.535 239.275 222.176 239.371L217.512 240.621C217.186 240.708 216.837 240.625 216.585 240.4L211.097 235.483L208.788 242.49C208.683 242.81 208.423 243.056 208.097 243.144L203.468 244.384C203.11 244.48 202.728 244.369 202.476 244.097C202.224 243.825 202.143 243.435 202.266 243.085L206.136 232.115L197.728 224.901C197.447 224.659 197.322 224.282 197.404 223.92C197.486 223.559 197.762 223.271 198.12 223.175L202.767 221.93ZM259.957 198.263L260.058 198.241C260.558 198.16 261.048 198.47 261.182 198.97L268.411 225.947C268.553 226.481 268.237 227.029 267.703 227.172L263.611 228.269C263.078 228.411 262.53 228.095 262.387 227.561L255.158 200.584L255.137 200.484C255.056 199.984 255.365 199.494 255.865 199.36L259.957 198.263ZM248.319 215.184C247.833 215.015 247.204 214.993 246.382 215.214C245.329 215.496 244.641 216.035 244.213 216.814C243.905 217.389 243.742 218.136 243.788 219.097L250.364 217.334C250.176 216.906 249.957 216.534 249.71 216.216L249.702 216.206C249.316 215.695 248.86 215.367 248.327 215.187L248.319 215.185L248.647 214.24L248.319 215.184Z"
                        fill="white"
                        stroke="#1C355A"
                        stroke-width="2"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M196.718 137.266C196.289 135.665 194.644 134.716 193.044 135.144L160.373 143.898C158.773 144.327 157.823 145.972 158.252 147.573L161.456 159.531C161.885 161.131 163.53 162.081 165.13 161.652L197.801 152.898C199.401 152.469 200.351 150.824 199.922 149.224L196.718 137.266Z"
                        fill="#DCDCDC"
                      />
                      <path
                        d="M241.102 125.373C240.673 123.773 239.028 122.823 237.427 123.252L204.757 132.006C203.157 132.435 202.207 134.08 202.636 135.68L205.84 147.638C206.269 149.238 207.914 150.188 209.514 149.759L242.184 141.005C243.785 140.577 244.734 138.932 244.306 137.331L241.102 125.373Z"
                        fill="#DCDCDC"
                      />
                      <path
                        d="M164.595 171.244C164.166 169.644 165.116 167.999 166.716 167.57L199.386 158.816C200.987 158.387 202.632 159.337 203.061 160.937L206.265 172.895C206.693 174.495 205.744 176.14 204.143 176.569L171.473 185.323C169.873 185.752 168.228 184.802 167.799 183.202L164.595 171.244Z"
                        fill="#DCDCDC"
                      />
                      <path
                        d="M247.444 149.045C247.015 147.444 245.37 146.494 243.77 146.923L211.1 155.677C209.499 156.106 208.55 157.751 208.978 159.351L212.182 171.309C212.611 172.91 214.256 173.859 215.857 173.431L248.527 164.677C250.127 164.248 251.077 162.603 250.648 161.002L247.444 149.045Z"
                        fill="#DCDCDC"
                      />
                      <path
                        d="M170.937 194.915C170.509 193.315 171.458 191.67 173.059 191.241L205.729 182.487C207.329 182.058 208.974 183.008 209.403 184.608L212.607 196.566C213.036 198.167 212.086 199.812 210.486 200.241L177.816 208.994C176.215 209.423 174.57 208.474 174.142 206.873L170.937 194.915Z"
                        fill="#DCDCDC"
                      />
                      <path
                        d="M253.787 172.716C253.358 171.115 251.713 170.166 250.113 170.594L217.442 179.348C215.842 179.777 214.892 181.422 215.321 183.023L218.525 194.981C218.954 196.581 220.599 197.531 222.199 197.102L254.87 188.348C256.47 187.919 257.42 186.274 256.991 184.674L253.787 172.716Z"
                        fill="#DCDCDC"
                      />
                      <path
                        d="M196.718 137.266C196.289 135.665 194.644 134.716 193.044 135.144L160.373 143.898C158.773 144.327 157.823 145.972 158.252 147.573L161.456 159.531C161.885 161.131 163.53 162.081 165.13 161.652L197.801 152.898C199.401 152.469 200.351 150.824 199.922 149.224L196.718 137.266Z"
                        stroke="#1C355A"
                        stroke-width="2"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M241.102 125.373C240.673 123.773 239.028 122.823 237.427 123.252L204.757 132.006C203.157 132.435 202.207 134.08 202.636 135.68L205.84 147.638C206.269 149.238 207.914 150.188 209.514 149.759L242.184 141.005C243.785 140.577 244.734 138.932 244.306 137.331L241.102 125.373Z"
                        stroke="#1C355A"
                        stroke-width="2"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M164.595 171.244C164.166 169.644 165.116 167.999 166.716 167.57L199.386 158.816C200.987 158.387 202.632 159.337 203.061 160.937L206.265 172.895C206.693 174.495 205.744 176.14 204.143 176.569L171.473 185.323C169.873 185.752 168.228 184.802 167.799 183.202L164.595 171.244Z"
                        stroke="#1C355A"
                        stroke-width="2"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M247.444 149.045C247.015 147.444 245.37 146.494 243.77 146.923L211.1 155.677C209.499 156.106 208.55 157.751 208.978 159.351L212.182 171.309C212.611 172.91 214.256 173.859 215.857 173.431L248.527 164.677C250.127 164.248 251.077 162.603 250.648 161.002L247.444 149.045Z"
                        stroke="#1C355A"
                        stroke-width="2"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M170.937 194.915C170.509 193.315 171.458 191.67 173.059 191.241L205.729 182.487C207.329 182.058 208.974 183.008 209.403 184.608L212.607 196.566C213.036 198.167 212.086 199.812 210.486 200.241L177.816 208.994C176.215 209.423 174.57 208.474 174.142 206.873L170.937 194.915Z"
                        stroke="#1C355A"
                        stroke-width="2"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M253.787 172.716C253.358 171.115 251.713 170.166 250.113 170.594L217.442 179.348C215.842 179.777 214.892 181.422 215.321 183.023L218.525 194.981C218.954 196.581 220.599 197.531 222.199 197.102L254.87 188.348C256.47 187.919 257.42 186.274 256.991 184.674L253.787 172.716Z"
                        stroke="#1C355A"
                        stroke-width="2"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M355.828 183.2C355.61 182.133 354.085 182.133 353.868 183.2L352.836 188.263C352.687 188.994 351.818 189.31 351.234 188.846L347.189 185.631C346.337 184.953 345.169 185.933 345.688 186.89L348.152 191.432C348.508 192.088 348.046 192.889 347.3 192.909L342.134 193.045C341.046 193.074 340.781 194.576 341.794 194.975L346.601 196.871C347.295 197.144 347.456 198.055 346.897 198.55L343.028 201.975C342.213 202.697 342.975 204.017 344.008 203.672L348.909 202.034C349.617 201.797 350.325 202.392 350.215 203.13L349.453 208.24C349.292 209.317 350.725 209.839 351.294 208.911L353.995 204.506C354.385 203.87 355.31 203.87 355.7 204.506L358.401 208.911C358.971 209.839 360.403 209.317 360.243 208.24L359.481 203.13C359.371 202.392 360.079 201.797 360.787 202.034L365.687 203.672C366.72 204.017 367.483 202.697 366.667 201.975L362.798 198.55C362.24 198.055 362.4 197.144 363.095 196.871L367.901 194.975C368.914 194.576 368.65 193.074 367.561 193.045L362.396 192.909C361.65 192.889 361.187 192.088 361.543 191.432L364.007 186.89C364.527 185.933 363.359 184.953 362.506 185.631L358.461 188.846C357.877 189.31 357.008 188.994 356.859 188.263L355.828 183.2Z"
                        fill="#FEEEDD"
                        stroke="#1C355A"
                        stroke-width="2"
                      />
                      <path
                        d="M60.8824 192.505C60.628 191.498 59.1977 191.498 58.9434 192.505L57.241 199.242C57.0883 199.846 56.4261 200.165 55.8585 199.907L49.5297 197.038C48.5841 196.609 47.6923 197.727 48.3207 198.554L52.5266 204.085C52.9039 204.582 52.7403 205.298 52.1852 205.581L45.9958 208.74C45.0709 209.212 45.3892 210.607 46.4273 210.631L53.3743 210.791C53.9974 210.806 54.4556 211.38 54.331 211.991L52.9417 218.8C52.7341 219.817 54.0227 220.438 54.6887 219.641L59.1457 214.31C59.5454 213.831 60.2804 213.831 60.6801 214.31L65.1371 219.641C65.803 220.438 67.0917 219.817 66.8841 218.8L65.4948 211.991C65.3702 211.38 65.8284 210.806 66.4515 210.791L73.3985 210.631C74.4366 210.607 74.7549 209.212 73.83 208.74L67.6406 205.581C67.0855 205.298 66.9219 204.582 67.2992 204.085L71.505 198.554C72.1335 197.727 71.2417 196.609 70.2961 197.038L63.9673 199.907C63.3997 200.165 62.7375 199.846 62.5848 199.242L60.8824 192.505Z"
                        fill="#FEEEDD"
                        stroke="#1C355A"
                        stroke-width="2"
                      />
                      <path
                        d="M106.156 48.2212L104.084 56.4227L96.3793 52.9295L101.499 59.6633L93.9647 63.5088L102.422 63.7043L100.73 71.9928L106.156 65.5027L111.582 71.9928L109.891 63.7043L118.348 63.5088L110.813 59.6633L115.933 52.9295L108.229 56.4227L106.156 48.2212Z"
                        fill="#FEEEDD"
                        stroke="#1C355A"
                        stroke-width="2"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M187.898 156.099C187.576 155.229 186.345 155.229 186.022 156.099L184.265 160.848C184.164 161.122 183.948 161.338 183.674 161.439L178.925 163.196C178.055 163.518 178.055 164.75 178.925 165.072L183.674 166.829C183.948 166.93 184.164 167.146 184.265 167.42L186.022 172.169C186.345 173.039 187.576 173.039 187.898 172.169L189.655 167.42C189.757 167.146 189.972 166.93 190.246 166.829L194.995 165.072C195.866 164.75 195.866 163.518 194.995 163.196L190.246 161.439C189.972 161.338 189.757 161.122 189.655 160.848L187.898 156.099Z"
                        fill="#FEEEDD"
                        stroke="#1C355A"
                        stroke-width="2"
                      />
                      <path
                        d="M67.8651 91.8466C68.7613 91.1776 69.913 92.3312 69.3291 93.285C64.3978 101.339 61.7317 109.937 61.7317 118.866C61.7317 153.382 101.564 182.95 158.115 195.279C93.8028 188.988 45.3938 161.562 45.3938 128.681C45.3938 115.105 53.6474 102.459 67.8651 91.8466Z"
                        fill="#FFF7FF"
                      />
                      <path
                        d="M61.7317 118.866L60.7317 118.866V118.866H61.7317ZM158.115 195.279L158.017 196.274C158.544 196.326 159.02 195.957 159.102 195.434C159.185 194.911 158.845 194.415 158.328 194.302L158.115 195.279ZM45.3938 128.681H44.3938H45.3938ZM69.3291 93.285L68.4762 92.7628L69.3291 93.285ZM67.8651 91.8466L67.267 91.0452L67.8651 91.8466ZM69.3291 93.285L68.4762 92.7628C63.4614 100.953 60.7317 109.73 60.7317 118.866L61.7317 118.866L62.7317 118.866C62.7317 110.144 65.3342 101.725 70.1819 93.8071L69.3291 93.285ZM61.7317 118.866H60.7317C60.7317 136.54 70.9316 152.822 88.1025 166.182C105.278 179.545 129.523 190.069 157.901 196.256L158.115 195.279L158.328 194.302C130.155 188.159 106.209 177.735 89.3306 164.603C72.4478 151.467 62.7317 135.708 62.7317 118.866H61.7317ZM158.115 195.279L158.212 194.284C126.158 191.148 98.1266 182.75 78.1413 171.088C58.1178 159.404 46.3938 144.591 46.3938 128.681H45.3938H44.3938C44.3938 145.652 56.8743 160.994 77.1333 172.815C97.4304 184.659 125.759 193.119 158.017 196.274L158.115 195.279ZM45.3938 128.681L46.3938 128.681C46.3938 115.543 54.3801 103.16 68.4633 92.6479L67.8651 91.8466L67.267 91.0452C52.9147 101.758 44.3938 114.666 44.3938 128.681H45.3938ZM69.3291 93.285L70.1819 93.8071C70.7591 92.8644 70.4502 91.8358 69.856 91.2469C69.2528 90.6491 68.1912 90.3553 67.267 91.0452L67.8651 91.8466L68.4633 92.6479C68.4774 92.6374 68.4778 92.6406 68.4642 92.6441C68.451 92.6476 68.437 92.6482 68.4262 92.6468C68.4081 92.6444 68.4215 92.6411 68.4483 92.6676C68.4746 92.6937 68.4851 92.7211 68.4872 92.7332C68.4879 92.7374 68.4875 92.7386 68.4875 92.7387C68.4874 92.7391 68.4871 92.7408 68.4858 92.7442C68.4845 92.7475 68.4818 92.7537 68.4762 92.7628L69.3291 93.285Z"
                        fill="#1C355A"
                      />
                      <path
                        d="M361.448 197.889C360.902 196.914 362.093 195.805 362.963 196.507C375.585 206.691 382.84 218.6 382.84 231.328C382.84 263.762 335.738 290.887 272.733 297.656C328.638 285.17 367.898 255.781 367.898 221.513C367.898 213.303 365.644 205.373 361.448 197.889Z"
                        fill="#FEEEDD"
                      />
                      <path
                        d="M382.84 231.328L383.84 231.328V231.328H382.84ZM272.733 297.656L272.515 296.68C271.998 296.796 271.66 297.296 271.747 297.819C271.833 298.342 272.313 298.707 272.84 298.651L272.733 297.656ZM367.898 221.513H368.898H367.898ZM362.963 196.507L363.59 195.729L362.963 196.507ZM361.448 197.889L360.576 198.378L361.448 197.889ZM362.963 196.507L362.335 197.285C374.821 207.36 381.84 219.011 381.84 231.328H382.84H383.84C383.84 218.189 376.348 206.022 363.59 195.729L362.963 196.507ZM382.84 231.328H381.84C381.84 247.022 370.434 261.647 350.902 273.257C331.407 284.846 304.028 293.288 272.627 296.662L272.733 297.656L272.84 298.651C304.444 295.255 332.118 286.75 351.924 274.976C371.695 263.223 383.84 248.069 383.84 231.328L382.84 231.328ZM272.733 297.656L272.951 298.632C301.006 292.366 324.946 281.847 341.898 268.539C358.845 255.235 368.898 239.059 368.898 221.513H367.898H366.898C366.898 238.234 357.322 253.887 340.663 266.966C324.007 280.042 300.365 290.46 272.515 296.68L272.733 297.656ZM367.898 221.513H368.898C368.898 213.113 366.591 205.017 362.32 197.4L361.448 197.889L360.576 198.378C364.698 205.73 366.898 213.492 366.898 221.513L367.898 221.513ZM362.963 196.507L363.59 195.729C362.693 195.005 361.622 195.258 360.997 195.834C360.382 196.4 360.036 197.415 360.576 198.378L361.448 197.889L362.32 197.4C362.315 197.391 362.313 197.384 362.312 197.381C362.31 197.377 362.31 197.376 362.31 197.375C362.31 197.375 362.31 197.373 362.311 197.369C362.313 197.357 362.325 197.33 362.352 197.305C362.379 197.28 362.392 197.284 362.373 197.286C362.362 197.287 362.347 197.286 362.334 197.282C362.321 197.277 362.321 197.274 362.335 197.285L362.963 196.507Z"
                        fill="#1C355A"
                      />
                      <path
                        d="M91.7819 140.596L82.4194 131.233"
                        stroke="#1C355A"
                        stroke-width="2.04327"
                        stroke-linecap="round"
                      />
                      <path
                        d="M100.132 149.452L96.3365 145.657"
                        stroke="#1C355A"
                        stroke-width="2.04327"
                        stroke-linecap="round"
                      />
                      <path
                        d="M317.122 138.951L303.458 125.287"
                        stroke="#1C355A"
                        stroke-width="2.04327"
                        stroke-linecap="round"
                      />
                      <path
                        d="M335.088 155.905L325.726 146.542"
                        stroke="#1C355A"
                        stroke-width="2.04327"
                        stroke-linecap="round"
                      />
                      <path
                        d="M70.0205 198.795L65.7188 194.493"
                        stroke="#1C355A"
                        stroke-width="2.04327"
                        stroke-linecap="round"
                      />
                      <path
                        d="M58.3807 188.167L46.2348 176.021"
                        stroke="#1C355A"
                        stroke-width="2.04327"
                        stroke-linecap="round"
                      />
                      <path
                        d="M329.774 125.287L303.458 98.971"
                        stroke="#1C355A"
                        stroke-width="2.04327"
                        stroke-linecap="round"
                      />
                      <path
                        d="M322.012 284.307C321.69 283.436 320.458 283.436 320.136 284.307L318.379 289.056C318.278 289.33 318.062 289.545 317.788 289.647L313.039 291.404C312.169 291.726 312.169 292.957 313.039 293.28L317.788 295.037C318.062 295.138 318.278 295.354 318.379 295.628L320.136 300.377C320.458 301.247 321.69 301.247 322.012 300.377L323.769 295.628C323.87 295.354 324.086 295.138 324.36 295.037L329.109 293.28C329.979 292.957 329.979 291.726 329.109 291.404L324.36 289.647C324.086 289.545 323.87 289.33 323.769 289.056L322.012 284.307Z"
                        fill="#FEEEDD"
                        stroke="#1C355A"
                        stroke-width="2"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3_9553">
                        <rect width="420" height="350" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
              )}
            </div>
          ) : (
            <div className="d-flex flex-column h-100">
              <h4 className="mb-3">
                Resultat fra opplasting av fil fra {uploadResults.membershipOwner?.name ?? 'Ukjent'}
                .{' '}
              </h4>
              <div className="mb-3 d-flex gap-3">
                <div className="badge bg-success fs-6 px-3 py-2">
                  Innmeldinger:{' '}
                  {uploadResults?.meldinger?.filter((m) => !m.medlemTilOgMedDato).length || 0}
                </div>
                <div className="badge bg-warning text-dark fs-6 px-3 py-2">
                  Utmeldinger:{' '}
                  {uploadResults?.meldinger?.filter((m) => m.medlemTilOgMedDato).length || 0}
                </div>
                <div className="badge bg-info text-dark fs-6 px-3 py-2">
                  Totalt: {uploadResults?.meldinger?.length || 0}
                </div>
              </div>
              <div className="table-responsive data-table-container">
                <Table striped bordered hover size="sm" className="wide-table">
                  <thead>
                    <tr>
                      <th>
                        <Form.Check
                          type="checkbox"
                          onChange={handleSelectAllRows}
                          checked={
                            selectedRows.length === uploadResults?.meldinger?.length &&
                            uploadResults?.meldinger?.length > 0
                          }
                        />
                      </th>
                      <th>Endring</th>
                      <th>GLN</th>
                      <th>Enhetnavn</th>
                      <th>Firmanavn</th>
                      <th>Org.nummer</th>
                      <th>Besøksadresse</th>
                      <th>Besøk Postnr</th>
                      <th>Besøk Poststed</th>
                      <th>Medlem hos</th>
                      <th>Medlemsnummer</th>
                      <th>Medlem fra og med</th>
                      <th>Medlem til og med</th>
                      <th>Telefon</th>
                      <th>Fakturaadresse</th>
                      <th>Faktura Postnr</th>
                      <th>Faktura Poststed</th>
                      <th>Postadresse</th>
                      <th>Postnummer</th>
                      <th>Post Poststed</th>
                      <th>Kommentar</th>
                      <th>GrossistKundenr</th>
                      <th>GrossistKundenrHos</th>
                      <th>Kontonummer</th>
                      <th>Salgsareal</th>
                      <th>Faktura Epost</th>
                      <th>Epost</th>
                      <th>Bransjeforslag</th>
                      <th>Dagligleder</th>
                      {/* <th>Status</th>
												<th>Melding</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResults?.meldinger?.map((result, index) => (
                      <tr key={index}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            onChange={() => handleRowCheck(index)}
                            checked={selectedRows.includes(index)}
                          />
                        </td>
                        <td>{result.medlemTilOgMedDato ? 'Utmelding' : 'Innmelding'}</td>
                        <td>{result.gln || '-'}</td>
                        <td>{result.enhetNavn || '-'}</td>
                        <td>{result.firmaNavn || '-'}</td>
                        <td>{result.organisasjonsnummer || '-'}</td>
                        <td>{result.besokAdresse || '-'}</td>
                        <td>{result.besokPostnummer || '-'}</td>
                        <td>{result.besokPoststed || '-'}</td>
                        <td>{result.medlemHos || '-'}</td>
                        <td>{result.medlemsNummer || '-'}</td>
                        <td>
                          {result.medlemFraOgMedDato
                            ? new Date(result.medlemFraOgMedDato).toLocaleDateString()
                            : '-'}
                        </td>
                        <td>
                          {result.medlemTilOgMedDato
                            ? new Date(result.medlemTilOgMedDato).toLocaleDateString()
                            : '-'}
                        </td>
                        <td>{result.telefon || '-'}</td>
                        <td>{result.fakturaAdresse || '-'}</td>
                        <td>{result.fakturaPostnummer || '-'}</td>
                        <td>{result.fakturaPoststed || '-'}</td>
                        <td>{result.postAdresse || '-'}</td>
                        <td>{result.postnummer || '-'}</td>
                        <td>{result.postPoststed || '-'}</td>
                        <td>{result.kommentar || '-'}</td>
                        <td>{result.grossistKundenummer || '-'}</td>
                        <td>{result.grossistKundenummerHos || '-'}</td>
                        <td>{result.kontonummer || '-'}</td>
                        <td>{result.salgsareal || '-'}</td>
                        <td>{result.fakturaEpost || '-'}</td>
                        <td>{result.epost || '-'}</td>
                        <td>{result.bransjeForslag || '-'}</td>
                        <td>{result.dagligLeder || '-'}</td>
                        {/* <td>
												<span className={`badge ${result.status === 'Valid' ? 'bg-success' : 'bg-warning'}`}>
													{result.status || '-'}
												</span>
												</td>
												<td>{result.message || '-'}</td> */}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex gap-2">
          <IcebergButton
            className="flex-fill"
            icon="close"
            variant="secondary"
            onClick={handleCloseModal}
          >
            Avbryt
          </IcebergButton>
          {(!uploadResults || uploadResults.meldinger?.length === 0) && (
            <IcebergButton
              disabled={!selectedFile || isUploading}
              className="flex-fill"
              variant="primary"
              onClick={handleUpload}
              icon="export"
            >
              Last opp
            </IcebergButton>
          )}
          {uploadResults && uploadResults.meldinger?.length > 0 && (
            <IcebergButton
              disabled={!selectedRows || selectedRows.length === 0 || isUploading}
              className="flex-fill"
              variant="primary"
              onClick={sendToQa}
              icon="send"
            >
              Send til QA
            </IcebergButton>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ChangeRequestUploadModal;
